import classnames from 'classnames';
import { Currency } from '~/components/Currency';
import type { Currency as CurrencyType } from '~/components/Currency/Currency.hooks';
import { useRate } from '~/components/Pair/Pair.hooks';
import { useMarketPolicy } from '~/components/Policy';
import styles from './Pair.module.scss';

export type PairProps = {
  primaryPrice: number;
  primaryCurrency: CurrencyType;
  secondaryPrice?: number | null;
  secondaryCurrency: CurrencyType | null;
  className?: string;
  reversed?: boolean;
  equal?: string;
};

export const Pair = ({
  primaryPrice,
  primaryCurrency,
  secondaryPrice,
  secondaryCurrency,
  reversed,
  equal = '≈',
  className,
}: PairProps) => {
  const { data: primaryPolicy } = useMarketPolicy(primaryCurrency);
  const { data: secondaryPolicy } = useMarketPolicy(secondaryCurrency!, {
    enabled: !!secondaryCurrency,
  });
  const { data: rate } = useRate(`${primaryCurrency}_${secondaryCurrency}`, {
    enabled: !!secondaryCurrency && !secondaryPrice,
    useErrorBoundary: false,
  });

  return (
    <span className={classnames(styles.root, 'pair', className)}>
      <Currency
        className={styles.primary}
        currency={primaryCurrency}
        value={primaryPrice}
        maximumFractionDigits={primaryPolicy?.maxDecimalPoint}
        minimumFractionDigits={0}
        reversed={reversed}
      />
      {secondaryCurrency && primaryCurrency !== secondaryCurrency && (
        <small className={styles.secondary}>
          <span className={styles.equal}>{equal}</span>
          <Currency
            currency={secondaryCurrency}
            value={secondaryPrice || primaryPrice * (rate?.price || 0)}
            maximumFractionDigits={secondaryPolicy?.maxDecimalPoint}
            minimumFractionDigits={0}
            reversed={reversed}
          />
        </small>
      )}
    </span>
  );
};
